'use client'

import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import { Resolver, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import { FileIcon, FileUpIcon, SaveIcon, XIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { FileSelector } from '@/components/partials/files'
import { useMetaStore } from '@/store'
import { CreateProductExcelImportParams, ProductExcelImportType } from '@/types/product-excel-import'
import { LoadingIndicator } from '@/components/partials'
import { FileType } from '@/types/file'
import { FormModeEnum, MimeTypes } from '@/enums'
import { CategoryCombobox } from '@/components/ui/category-combobox'
import { setValidationErrors } from '@/lib/utils'

type PropsType = {
  initialData?: ProductExcelImportType | null
  onSubmit: (data: CreateProductExcelImportParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type ProductExcelImportFormRef = {
  handleValidationErrors: (error: any) => void
}

type FormData = {
  categoryId: string
  fileId: string
}

const ProductExcelImportForm = forwardRef<ProductExcelImportFormRef, PropsType>(
  ({ initialData, onSubmit, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const { metaState } = useMetaStore()

    // State for a file
    const [file, setFile] = useState<FileType | null>(initialData?.file ?? null)

    const schema = useMemo(() => {
      return z.object({
        categoryId: z.string().min(1, t('required')),
        fileId: z.string().min(1, t('required'))
      })
    }, [t])

    const getDefaultValues = useCallback((): FormData => {
      return {
        categoryId: initialData?.category?.id ?? '',
        fileId: initialData?.file?.id ?? ''
      }
    }, [initialData])

    const form = useForm<FormData>({
      resolver: zodResolver(schema) as Resolver<FormData>,
      defaultValues: getDefaultValues(),
      mode: 'onChange'
    })

    // Expose handleValidationErrors to parent via ref
    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
      }
    }))

    const formErrors = form.formState.errors

    const handleFileChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setFile(null)
        form.setValue('fileId', '', { shouldValidate: true })
      } else if ('length' in files) {
        setFile(files[0])
        form.setValue('fileId', files[0]?.id || '', { shouldValidate: true })
      } else {
        setFile(files)
        form.setValue('fileId', files?.id || '', { shouldValidate: true })
      }
    }

    const handleSubmit = (data: FormData) => {
      onSubmit({
        categoryId: data.categoryId,
        fileId: data.fileId
      })
    }

    if (!metaState.response?.locales) {
      return <LoadingIndicator />
    }

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileUpIcon className="h-5 w-5" />
                {t('import_as_excel')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Category */}
              <FormField
                control={form.control}
                name="categoryId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required className="flex items-center gap-2">
                      {t('category')}
                    </FormLabel>
                    <FormControl>
                      <CategoryCombobox
                        value={field.value}
                        onValueChange={v => field.onChange(v)}
                        disabled={isLoading}
                        clearable
                        error={!!formErrors.categoryId}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* File */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name={'fileId'}
                  render={({ field }) => <input type="hidden" {...field} value={field.value || ''} />}
                />
                <div className="flex items-center gap-2">
                  <FileIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('file')}</h3>
                </div>
                <div>
                  <FileSelector
                    multiple={false}
                    onlyImages={false}
                    optimizeImageSelection={false}
                    types={[MimeTypes.XLSX]}
                    value={file}
                    onChange={handleFileChange}
                    buttonLabel={t('select_file')}
                    buttonLabelChange={t('change_file')}
                    noSelectionText={t('no_file_selected')}
                    allowMainSelection={true}
                    initialMainId={initialData?.file?.id}
                  />
                  {formErrors.fileId && (
                    <p className="text-destructive mt-2 text-sm">
                      {String(formErrors.fileId.message || t('required'))}
                    </p>
                  )}
                </div>
              </div>

              <Separator />

              {/* Form actions */}
              <div className="flex items-center justify-between gap-3 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading || form.formState.isSubmitting}>
                  <XIcon className="h-4 w-4" />
                  {t('cancel')}
                </Button>
                <Button type="submit" disabled={isLoading || form.formState.isSubmitting}>
                  <SaveIcon className="h-4 w-4" />
                  {isLoading || form.formState.isSubmitting ? t('saving') : t(mode)}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </Form>
    )
  }
)

ProductExcelImportForm.displayName = 'ProductExcelImportForm'

export default ProductExcelImportForm
